import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import type { Company, CompanySite } from './../types/Companies';
import type { BaseProperty, SelectDropdownItem } from '@/types/Base';
import { getCompanies, getCompanySites } from '@/services/api/companies';
import type { MaterialType, MeasurementUnit, AlterCodeType } from '@/types/Materials';
import {
  getAlterCodeTypes,
  getMaterialTypes,
  getMeasurementUnits,
  getWarehouses,
} from '@/services/api/materials';
import { getInstallation, getMWCountries, getMWRouteTypes } from '@/services/api/mw';
import type { VatCategory, VatLevel } from '@/types/Vat';
import { getVatCategories, getVatLevels } from '@/services/api/vat';
import {
  type MWApplication,
  type MWModule,
  type DataType,
  type MWAccountGroup,
  type MWUserType,
  type ModuleInstallation,
  type MWRouteTypes,
  type MWCountry,
} from '@/types/MW';
import {
  getDataTypes,
  getMWAccountGroups,
  getMWApplications,
  getMWModules,
  getMWUserTypes,
} from '@/services/api/mw';
import type { ColorCatalog, SizeCatalog, StyleCatalog } from '@/types/Catalogs';
import { getColorCatalogs, getSizeCatalogs, getStyleCatalogs } from '@/services/api/catalogs';
import type { PaymentType } from '@/types/PaymentType';
import type { Representative, Competitor, CompetitorType } from '@/types/Traders';
import {
  getCompetitors,
  getCompetitorTypes,
  getRepresentativesWithConditions,
  getTaxOffices,
} from '@/services/api/traders';
import {
  getDeliveryTypes,
  getDocumentStatuses,
  getExpensesAndCharges,
  getPaymentTypes,
  getAllDeviceConfigurations,
  getAllDocumentSeriesTypes,
  getAllDocumentSeries,
  getAllDistributionChannels,
} from '@/services/api/documents';
import { getDivisions, getDocumentTypes } from '@/services/api/documents';
import type { Division } from '@/types/Division';
import type { User } from '@/types/Users';
import {
  getAllUsers,
  getSalesPersonAccessLevels,
  getSalesPersons,
  getSalesPersonTypes,
} from '@/services/api/user';
import type { DocumentSeries } from '@/types/DocumentSeries';
import type { DocumentType } from '@/types/DocumentTypes';
import type { DistributionChannel } from '@/types/DistributionChannel';
import type { GroupItem, GroupItemWithAncestry } from '@/types/Groups';
import { getAllRootGroupItems, getGroupItemsWithTradeSites } from '@/services/api/groups';
import type { DocumentStatus } from '@/types/DocumentStatus';
import type { DeliveryType } from '@/types/DeliveryTypes';
import type { RouteDefinitions } from '@/types/Routes';
import { getRouteDefinitions } from '@/services/api/routes';
import {
  type SalesPerson,
  type SalesPersonsAccessLevel,
  type SalesPersonType,
} from '@/types/SalesPersons';
import type { Warehouse } from '@/types/Warehouse';
import { eChargesType, type ExpensesAndCharges } from '@/types/Documents';
import type { DeviceConfiguration } from '@/types/DeviceConfiguration';
import type { DocumentSeriesType } from '@/types/DocumentSeriesTypes';
import type { Range } from '@/types/Ranges';
import { getRanges } from '@/services/api/ranges';
import type { ReportingTemplate } from '@/types/Reporting';
import { getReportingTemplatesByCondition } from '@/services/api/reporting';
import { MWAccountGroups, MWEntities } from '@/utils/constants';

export const useLookupsStore = defineStore('lookups', () => {
  const companies = ref<Company[]>([]);

  const dropdownCompanies = computed<SelectDropdownItem[]>(() => {
    return companies.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });

  async function getCompaniesLookup() {
    if (companies.value.length == 0) companies.value = await getCompanies();
  }

  const divisions = ref<Division[]>([]);

  const dropdownDivisions = computed<SelectDropdownItem[]>(() => {
    return divisions.value.map((item) => ({
      value: item.id,
      name: item.name ? item.name : '',
    }));
  });

  async function getDivisionsLookup() {
    if (divisions.value.length == 0) divisions.value = await getDivisions();
  }

  const materialTypes = ref<MaterialType[]>([]);

  const dropdownMaterialTypes = computed<SelectDropdownItem[]>(() => {
    return materialTypes.value.map((items) => ({
      value: items.id,
      name: items.name,
    }));
  });

  async function getMaterialTypesLookup() {
    {
      if (materialTypes.value.length == 0) materialTypes.value = await getMaterialTypes();
    }
  }

  const vatCategories = ref<VatCategory[]>([]);

  const dropdownVatCategories = computed<SelectDropdownItem[]>(() => {
    return vatCategories.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });

  async function getVatCategoriesLookup() {
    if (vatCategories.value.length == 0) vatCategories.value = await getVatCategories();
  }

  const mwAccountGroups = ref<MWAccountGroup[]>([]);

  const dropdownMwAccountGroups = computed<SelectDropdownItem[]>(() => {
    return mwAccountGroups.value.map((item) => ({
      value: item.id,
      name: item.accountGroup,
    }));
  });

  const dropdownMWAccountGroupsMaterials = computed<SelectDropdownItem[]>(() => {
    return mwAccountGroups.value
      .filter(
        (item) =>
          item.mwEntity === MWEntities.Materials && item.accountGroup !== MWAccountGroups.AlterCode
      )
      .map((item) => ({
        value: item.id,
        name: item.accountGroup,
      }));
  });

  function getDropdownMWAccountGroupsByCategory(category: string): SelectDropdownItem[] {
    return mwAccountGroups.value
      .filter((item) => item.accountGroup.toLocaleLowerCase() === category.toLocaleLowerCase())
      .map((item) => ({
        value: item.id,
        name: item.accountGroup,
      }));
  }

  async function getMwAccountGroupsLookup() {
    if (mwAccountGroups.value.length == 0) mwAccountGroups.value = await getMWAccountGroups();
  }

  const colors = ref<ColorCatalog[]>([]);

  const dropdownColors = computed<SelectDropdownItem[]>(() => {
    return colors.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });

  async function getColorsLookup() {
    if (colors.value.length == 0) colors.value = await getColorCatalogs();
  }

  const sizes = ref<SizeCatalog[]>([]);

  const dropdownSizes = computed<SelectDropdownItem[]>(() => {
    return sizes.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });

  async function getSizesLookup() {
    if (sizes.value.length == 0) sizes.value = await getSizeCatalogs();
  }

  const styles = ref<StyleCatalog[]>([]);

  const dropdownStyles = computed<SelectDropdownItem[]>(() => {
    return styles.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });

  async function getStylesLookup() {
    if (styles.value.length == 0) styles.value = await getStyleCatalogs();
  }

  const paymentTypes = ref<PaymentType[]>([]);

  const dropdownPaymentTypes = computed<SelectDropdownItem[]>(() => {
    return paymentTypes.value.map((item) => ({
      value: item.id,
      name: item.name ?? item.eType?.toString() ?? '',
    }));
  });

  async function getPaymentTypesLookup() {
    if (paymentTypes.value.length == 0) paymentTypes.value = await getPaymentTypes();
  }

  const measurementUnits = ref<MeasurementUnit[]>([]);

  const dropdownMeasurementUnits = computed<SelectDropdownItem[]>(() => {
    return measurementUnits.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });

  async function getMeasurementUnitsLookup() {
    if (measurementUnits.value.length == 0) measurementUnits.value = await getMeasurementUnits();
  }

  const alterCodeTypes = ref<AlterCodeType[]>([]);

  const dropDownAlterCodeTypes = computed<SelectDropdownItem[]>(() => {
    return alterCodeTypes.value.map((item) => ({
      value: item.id,
      name: item.name || '',
    }));
  });

  async function getAlterCodeTypesLookup() {
    if (alterCodeTypes.value.length == 0) alterCodeTypes.value = await getAlterCodeTypes();
  }

  const competitors = ref<Partial<Competitor>[]>([]);

  const dropDownCompetitors = computed<SelectDropdownItem[]>(() => {
    return competitors.value.map(
      (item) =>
        ({
          value: item.id,
          name: item.code,
        }) as SelectDropdownItem
    );
  });

  async function getCompetitorsLookup() {
    if (competitors.value.length == 0) competitors.value = await getCompetitors();
  }

  const users = ref<User[]>([]);
  const dropdownUsers = computed<SelectDropdownItem[]>(() => {
    return users.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getUsersLookup() {
    if (users.value.length == 0) users.value = await getAllUsers();
  }

  const userTypes = ref<MWUserType[]>([]);
  const dropdownUserTypes = computed<SelectDropdownItem[]>(() => {
    return userTypes.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getUserTypesLookup() {
    if (userTypes.value.length == 0) userTypes.value = await getMWUserTypes();
  }

  const documentSeries = ref<DocumentSeries[]>([]);
  const dropdownDocumentSeries = computed<SelectDropdownItem[]>(() => {
    return documentSeries.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getDocumentSeriesLookup() {
    if (documentSeries.value.length === 0) documentSeries.value = await getAllDocumentSeries();
  }

  const documentTypes = ref<DocumentType[]>([]);
  const dropdownDocumentTypes = computed<SelectDropdownItem[]>(() => {
    return documentTypes.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getDocumentTypesLookup() {
    if (documentTypes.value.length == 0) documentTypes.value = await getDocumentTypes();
  }

  const distributionChannels = ref<DistributionChannel[]>([]);
  const dropdownDistributionChannels = computed<SelectDropdownItem[]>(() => {
    return distributionChannels.value.map((item) => ({
      value: item.id,
      name: item.company?.name ?? '',
    }));
  });
  async function getDistributionChannelsLookup() {
    if (distributionChannels.value.length == 0)
      distributionChannels.value = await getAllDistributionChannels();
  }

  const traderGroupItemNames = ref<GroupItemWithAncestry[]>([]);
  const dropdownTraderGroupItemNames = computed<SelectDropdownItem[]>(() => {
    return traderGroupItemNames.value.map((item) => ({
      value: item.id,
      name: item.ancestry ?? '',
    }));
  });
  async function getTraderGroupItemNames() {
    if (traderGroupItemNames.value.length == 0) {
      const response = await getGroupItemsWithTradeSites({});
      if (response) traderGroupItemNames.value = response.data;
    }
  }

  const rootGroupItems = ref<GroupItem[]>([]);
  const dropdownrootGroupItems = computed<SelectDropdownItem[]>(() => {
    return rootGroupItems.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getRootGroupItemsLookup() {
    if (rootGroupItems.value.length == 0) {
      const response = await getAllRootGroupItems();
      if (response) rootGroupItems.value = response;
    }
  }

  const competitorTypes = ref<CompetitorType[]>([]);
  const dropdownCompetitorTypes = computed<SelectDropdownItem[]>(() => {
    return competitorTypes.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getCompetitorTypesLookup() {
    if (competitorTypes.value.length == 0) competitorTypes.value = await getCompetitorTypes();
  }

  const documentStatuses = ref<DocumentStatus[]>([]);
  const dropdownDocumentStatuses = computed<SelectDropdownItem[]>(() => {
    return documentStatuses.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getDocumentStatusesLookup() {
    if (documentStatuses.value.length == 0) documentStatuses.value = await getDocumentStatuses();
  }

  const deliveryTypes = ref<DeliveryType[]>([]);
  const dropdownDeliveryTypes = computed<SelectDropdownItem[]>(() => {
    return deliveryTypes.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getDeliveryTypesLookup() {
    if (deliveryTypes.value.length == 0) deliveryTypes.value = await getDeliveryTypes();
  }

  const companySites = ref<CompanySite[]>([]);
  const dropdownCompanySites = computed<SelectDropdownItem[]>(() => {
    return companySites.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getCompanySitesLookup() {
    if (companySites.value.length == 0) companySites.value = await getCompanySites();
  }

  const routeTypes = ref<MWRouteTypes[]>([]);
  const dropdownRouteTypes = computed<SelectDropdownItem[]>(() => {
    return routeTypes.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getRouteTypesLookup() {
    if (routeTypes.value.length == 0) routeTypes.value = await getMWRouteTypes();
  }

  const routeDefinitions = ref<RouteDefinitions[]>([]);
  const dropdownRouteDefinitions = computed<SelectDropdownItem[]>(() => {
    return routeDefinitions.value.map((item) => ({
      value: item.id,
      name: item.name ?? '',
    }));
  });
  async function getRouteDefinitionsLookup() {
    if (routeDefinitions.value.length == 0) routeDefinitions.value = await getRouteDefinitions();
  }

  const salesPersonTypes = ref<SalesPersonType[]>([]);
  const dropdownSalespersonTypes = computed<SelectDropdownItem[]>(() => {
    return salesPersonTypes.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getSalespersonTypesLookup() {
    if (salesPersonTypes.value.length == 0) salesPersonTypes.value = await getSalesPersonTypes();
  }

  const salesPersonsAccessLevels = ref<SalesPersonsAccessLevel[]>([]);
  const dropdownSalesPersonsAccessLevels = computed<SelectDropdownItem[]>(() => {
    return salesPersonsAccessLevels.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getSalesPersonsAccessLevelsLookup() {
    if (salesPersonsAccessLevels.value.length == 0)
      salesPersonsAccessLevels.value = await getSalesPersonAccessLevels();
  }

  const salespersons = ref<SalesPerson[]>([]);
  const dropDownSalesPersons = computed<SelectDropdownItem[]>(() => {
    return salespersons.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getSalesPersonsLookup() {
    if (salespersons.value.length == 0) salespersons.value = await getSalesPersons();
  }

  const mwModules = ref<MWModule[]>([]);
  const dropdownMwModules = computed<SelectDropdownItem[]>(() => {
    return mwModules.value.map((item) => ({
      value: item.id,
      name: item.displayName,
    }));
  });
  async function getMwModulesLookup() {
    if (mwModules.value.length == 0) mwModules.value = (await getMWModules()).data;
  }

  const mwApplications = ref<MWApplication[]>([]);
  const dropdownMwApplications = computed<SelectDropdownItem[]>(() => {
    return mwApplications.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getMwApplicationLookup() {
    if (mwApplications.value.length == 0) mwApplications.value = (await getMWApplications()).data;
  }

  const moduleInstallations = ref<ModuleInstallation[]>([]);
  async function getModuleInstalationsLookup() {
    if (moduleInstallations.value.length === 0) moduleInstallations.value = await getInstallation();
  }

  const dataTypes = ref<DataType[]>([]);
  const dropdownDataTypes = computed<SelectDropdownItem[]>(() => {
    return dataTypes.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getDataTypesLookup() {
    if (dataTypes.value.length == 0) dataTypes.value = await getDataTypes();
  }

  const warehouses = ref<Warehouse[]>([]);
  const dropdownWarehouses = computed<SelectDropdownItem[]>(() => {
    return warehouses.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getWarehousesLookup() {
    if (warehouses.value.length == 0) warehouses.value = await getWarehouses();
  }

  const vatLevels = ref<VatLevel[]>([]);

  const dropdownVatLevels = computed<SelectDropdownItem[]>(() => {
    return vatLevels.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });

  async function getVatLevelsLookup() {
    if (vatLevels.value.length == 0) vatLevels.value = await getVatLevels();
  }

  const expensesAndCharges = ref<ExpensesAndCharges[]>([]);
  const dropDownExpensesAndCharges = computed<SelectDropdownItem[]>(() => {
    return expensesAndCharges.value
      .filter(
        (item) =>
          item.chargeType === eChargesType.Charge || item.chargeType === eChargesType.Expense
      )
      .map((item) => ({
        value: item.id,
        name: item.description,
      }));
  });

  const dropDownTaxes = computed<SelectDropdownItem[]>(() => {
    return expensesAndCharges.value
      .filter((item) => item.chargeType === eChargesType.Tax)
      .map((item) => ({
        value: item.id,
        name: item.description,
      }));
  });
  async function getExpensesAndChargesLookup() {
    if (expensesAndCharges.value.length == 0)
      expensesAndCharges.value = await getExpensesAndCharges();
  }

  const deviceConfigurations = ref<Partial<DeviceConfiguration>[]>([]);
  async function getDeviceConfigurationsLookup() {
    if (deviceConfigurations.value.length == 0)
      deviceConfigurations.value = await getAllDeviceConfigurations();
  }

  const documentSeriesTypes = ref<Partial<DocumentSeriesType>[]>([]);
  async function getDocumentSeriesTypesLookup() {
    if (documentSeriesTypes.value.length == 0)
      documentSeriesTypes.value = await getAllDocumentSeriesTypes();
  }

  const lookupDependencies: Record<string, string[]> = {
    companySites: ['routeDefinitions'],
    salesPersonTypes: ['salesPersonsAccessLevels'],
    divisions: ['documentTypes'],
  };

  const mwCountries = ref<MWCountry[]>([]);
  const dropdownMWCountries = computed<SelectDropdownItem[]>(() => {
    return mwCountries.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getMWCountriesLookup() {
    if (mwCountries.value.length == 0) mwCountries.value = await getMWCountries();
  }

  const taxOffices = ref<BaseProperty[]>([]);
  const dropDownTaxOffices = computed<SelectDropdownItem[]>(() => {
    return taxOffices.value.map((item) => ({
      value: item.id,
      name: item.name || '',
    }));
  });
  async function getTaxOfficesLookup() {
    if (taxOffices.value.length == 0) taxOffices.value = await getTaxOffices();
  }

  const ranges = ref<Range[]>([]);
  const dropdownRanges = computed<SelectDropdownItem[]>(() => {
    return ranges.value.map((item) => ({
      value: item.id,
      name: item.name,
    }));
  });
  async function getRangesLookup() {
    if (ranges.value.length == 0) ranges.value = await getRanges();
  }

  const reportingTemplates = ref<ReportingTemplate[]>([]);
  const dropdownReportingTemplates = computed<SelectDropdownItem[]>(() => {
    return reportingTemplates.value.map((item) => ({
      value: item.id,
      name: item.name || '',
    }));
  });
  async function getReportingTemplatesLookup() {
    if (reportingTemplates.value.length == 0)
      reportingTemplates.value = (await getReportingTemplatesByCondition({})).data;
  }

  const representatives = ref<Representative[]>([]);
  const dropdownRepresentatives = computed<SelectDropdownItem[]>(() => {
    return representatives.value.map((item) => ({
      value: item.id,
      name: item.name || '',
    }));
  });
  async function getRepresentativesLookup() {
    if (representatives.value.length == 0)
      representatives.value = (await getRepresentativesWithConditions({})).data;
  }

  const lookupFunctions: Record<string, () => Promise<void>> = {
    companies: getCompaniesLookup,
    companySites: getCompanySitesLookup,
    routeDefinitions: getRouteDefinitionsLookup,
    divisions: getDivisionsLookup,
    materialTypes: getMaterialTypesLookup,
    vatCategories: getVatCategoriesLookup,
    mwAccountGroups: getMwAccountGroupsLookup,
    colors: getColorsLookup,
    sizes: getSizesLookup,
    styles: getStylesLookup,
    paymentTypes: getPaymentTypesLookup,
    measurementUnits: getMeasurementUnitsLookup,
    alterCodeTypes: getAlterCodeTypesLookup,
    competitors: getCompetitorsLookup,
    users: getUsersLookup,
    userTypes: getUserTypesLookup,
    documentSeries: getDocumentSeriesLookup,
    documentTypes: getDocumentTypesLookup,
    distributionChannels: getDistributionChannelsLookup,
    traderGroupItemNames: getTraderGroupItemNames,
    rootGroupItems: getRootGroupItemsLookup,
    competitorTypes: getCompetitorTypesLookup,
    documentStatuses: getDocumentStatusesLookup,
    deliveryTypes: getDeliveryTypesLookup,
    salespersons: getSalesPersonsLookup,
    salesPersonTypes: getSalespersonTypesLookup,
    salesPersonsAccessLevels: getSalesPersonsAccessLevelsLookup,
    mwModules: getMwModulesLookup,
    mwApplications: getMwApplicationLookup,
    dataTypes: getDataTypesLookup,
    warehouses: getWarehousesLookup,
    vatLevels: getVatLevelsLookup,
    deviceConfigurations: getDeviceConfigurationsLookup,
    documentSeriesTypes: getDocumentSeriesLookup,
    mwCountries: getMWCountriesLookup,
    taxOffices: getTaxOfficesLookup,
    ranges: getRangesLookup,
    reportingTemplates: getReportingTemplatesLookup,
    representatives: getRepresentativesLookup,
    moduleInstallations: getModuleInstalationsLookup,
  };

  const clearFunctions: Record<string, () => void> = {
    companies: () => {
      companies.value = [];
    },
    companySites: () => {
      companySites.value = [];
    },
    routeDefinitions: () => {
      routeDefinitions.value = [];
    },
    divisions: () => {
      divisions.value = [];
    },
    materialTypes: () => {
      materialTypes.value = [];
    },
    vatCategories: () => {
      vatCategories.value = [];
    },
    mwAccountGroups: () => {
      mwAccountGroups.value = [];
    },
    colors: () => {
      colors.value = [];
    },
    sizes: () => {
      sizes.value = [];
    },
    styles: () => {
      styles.value = [];
    },
    paymentTypes: () => {
      paymentTypes.value = [];
    },
    measurementUnits: () => {
      measurementUnits.value = [];
    },
    alterCodeTypes: () => {
      alterCodeTypes.value = [];
    },
    competitors: () => {
      competitors.value = [];
    },
    users: () => {
      users.value = [];
    },
    userTypes: () => {
      userTypes.value = [];
    },
    documentSeries: () => {
      documentSeries.value = [];
    },
    documentTypes: () => {
      documentTypes.value = [];
    },
    distributionChannels: () => {
      distributionChannels.value = [];
    },
    traderGroupItemNames: () => {
      traderGroupItemNames.value = [];
    },
    rootGroupItems: () => {
      rootGroupItems.value = [];
    },
    competitorTypes: () => {
      competitorTypes.value = [];
    },
    documentStatuses: () => {
      documentStatuses.value = [];
    },
    deliveryTypes: () => {
      deliveryTypes.value = [];
    },
    salespersons: () => {
      salespersons.value = [];
    },
    salesPersonTypes: () => {
      salesPersonTypes.value = [];
    },
    salesPersonsAccessLevels: () => {
      salesPersonsAccessLevels.value = [];
    },
    mwModules: () => {
      mwModules.value = [];
    },
    mwApplications: () => {
      mwApplications.value = [];
    },
    dataTypes: () => {
      dataTypes.value = [];
    },
    warehouses: () => {
      warehouses.value = [];
    },
    vatLevels: () => {
      vatLevels.value = [];
    },
    deviceConfigurations: () => {
      deviceConfigurations.value = [];
    },
    documentSeriesTypes: () => {
      documentSeriesTypes.value = [];
    },
    mwCountries: () => {
      mwCountries.value = [];
    },
    taxOffices: () => {
      taxOffices.value = [];
    },
    ranges: () => {
      ranges.value = [];
    },
    reportingTemplates: () => {
      reportingTemplates.value = [];
    },
    representatives: () => {
      representatives.value = [];
    },
    moduleInstallations: () => {
      moduleInstallations.value = [];
    },
  };

  async function reloadLookups(lookupNames: string[], clearBeforeReload = true) {
    lookupNames.forEach((lookupName) => {
      reloadSingleLookup(lookupName, clearBeforeReload);
    });
  }

  async function reloadSingleLookup(lookupName: string, clearBeforeReload = true) {
    if (clearBeforeReload) {
      clearFunctions[lookupName]();
    }

    await lookupFunctions[lookupName]();

    const dependencies = lookupDependencies[lookupName] || [];
    for (const dependentLookup of dependencies) {
      await reloadSingleLookup(dependentLookup, clearBeforeReload);
    }
  }

  async function reloadAllLookups(clearBeforeReload = true) {
    for (const lookupName of Object.keys(lookupFunctions)) {
      await reloadSingleLookup(lookupName, clearBeforeReload);
    }
  }

  return {
    reloadLookups,
    reloadAllLookups,
    reloadSingleLookup,
    companies,
    dropdownCompanies,
    getCompaniesLookup,
    divisions,
    dropdownDivisions,
    getDivisionsLookup,
    materialTypes,
    dropdownMaterialTypes,
    getMaterialTypesLookup,
    vatCategories,
    dropdownVatCategories,
    getVatCategoriesLookup,
    mwAccountGroups,
    dropdownMwAccountGroups,
    dropdownMWAccountGroupsMaterials,
    getDropdownMWAccountGroupsByCategory,
    getMwAccountGroupsLookup,
    colors,
    dropdownColors,
    getColorsLookup,
    sizes,
    dropdownSizes,
    getSizesLookup,
    styles,
    dropdownStyles,
    getStylesLookup,
    paymentTypes,
    dropdownPaymentTypes,
    getPaymentTypesLookup,
    measurementUnits,
    dropdownMeasurementUnits,
    getMeasurementUnitsLookup,
    alterCodeTypes,
    dropDownAlterCodeTypes,
    getAlterCodeTypesLookup,
    dropDownCompetitors,
    getCompetitorsLookup,
    users,
    dropdownUsers,
    getUsersLookup,
    userTypes,
    dropdownUserTypes,
    getUserTypesLookup,
    documentSeries,
    dropdownDocumentSeries,
    getDocumentSeriesLookup,
    documentTypes,
    dropdownDocumentTypes,
    getDocumentTypesLookup,
    distributionChannels,
    dropdownDistributionChannels,
    getDistributionChannelsLookup,
    traderGroupItemNames,
    getTraderGroupItemNames,
    dropdownTraderGroupItemNames,
    rootGroupItems,
    dropdownrootGroupItems,
    getRootGroupItemsLookup,
    competitorTypes,
    dropdownCompetitorTypes,
    getCompetitorTypesLookup,
    documentStatuses,
    dropdownDocumentStatuses,
    getDocumentStatusesLookup,
    deliveryTypes,
    dropdownDeliveryTypes,
    getDeliveryTypesLookup,
    companySites,
    getCompanySitesLookup,
    dropdownCompanySites,
    routeDefinitions,
    getRouteDefinitionsLookup,
    dropdownRouteDefinitions,
    dropdownSalespersonTypes,
    getSalespersonTypesLookup,
    dropdownSalesPersonsAccessLevels,
    getSalesPersonsAccessLevelsLookup,
    salespersons,
    dropDownSalesPersons,
    getSalesPersonsLookup,
    salesPersonTypes,
    mwModules,
    dropdownMwModules,
    getMwModulesLookup,
    getMwApplicationLookup,
    mwApplications,
    dropdownMwApplications,
    moduleInstallations,
    getModuleInstalationsLookup,
    dropdownDataTypes,
    getDataTypesLookup,
    dataTypes,
    warehouses,
    dropdownWarehouses,
    getWarehousesLookup,
    vatLevels,
    dropdownVatLevels,
    getVatLevelsLookup,
    expensesAndCharges,
    dropDownExpensesAndCharges,
    dropDownTaxes,
    getExpensesAndChargesLookup,
    deviceConfigurations,
    getDeviceConfigurationsLookup,
    documentSeriesTypes,
    getDocumentSeriesTypesLookup,
    routeTypes,
    dropdownRouteTypes,
    getRouteTypesLookup,
    dropdownMWCountries,
    getMWCountriesLookup,
    dropDownTaxOffices,
    getTaxOfficesLookup,
    getRangesLookup,
    dropdownRanges,
    ranges,
    reportingTemplates,
    dropdownReportingTemplates,
    getReportingTemplatesLookup,
    representatives,
    dropdownRepresentatives,
    getRepresentativesLookup,
  };
});
